/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import type { CommunityBroadcast } from "@packages/sdk";
import { getFullName, useDate } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  colors,
  cssMediaQueryMinWidthTablet,
  radius,
  spacing,
  Text,
} from "@packages/ui";
import type { HTMLAttributes } from "react";

import { RemoveBroadcastDropdownMenu } from "./RemoveBroadcastDropdownMenu";
import { SentToModal } from "./SentToModal";

const broadcastMessageStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const contentContainerStyles: Interpolation = {
  backgroundColor: colors.purple20,
  borderRadius: radius.l,
  color: colors.white,
  padding: spacing.xl,
};

const headerStyles: Interpolation = {
  display: "flex",
  gap: spacing.s,
  alignItems: "flex-start",
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
  },
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
  minWidth: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

type BroadcastMessageProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: CommunityBroadcast;
};

export const BroadcastMessage = ({ data, ...props }: BroadcastMessageProps) => {
  const { getDateFormatDifferenceNow } = useDate();
  const t = useTranslations();

  return (
    <div css={broadcastMessageStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.broadcaster_user?.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text size="xl" type="detail">
            <strong>
              {data?.broadcaster_user
                ? getFullName({
                    firstName: data?.broadcaster_user?.name,
                    lastName: data?.broadcaster_user?.last_name,
                  })
                : t("direct_messages_deleted_user")}
            </strong>
            &nbsp;
            <I18nMessage message="dashboard_broadcast_sent_message" />
            &nbsp;
            <SentToModal
              communityId={data.broadcaster_community?.id}
              data={data}
            />
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
          </Text>
        </div>
        <RemoveBroadcastDropdownMenu
          broadcastId={data.id}
          communityId={data.broadcaster_community?.id}
        />
      </div>
      <div css={contentContainerStyles}>
        <Text as="p" size="l" type="body">
          {data.content}
        </Text>
      </div>
    </div>
  );
};

BroadcastMessage.displayName = "BroadcastMessage";
